// Photographic proof-of-delivery — the verification moat made visible. The
// assigned merchant captures a photo at handoff (canUpload); every party sees
// it on the order and in the receipt. Photos are downscaled client-side before
// upload to keep them small.

import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, ImageIcon, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { proofs, type ProofMeta } from '../../lib/api';
import { isNative } from '../../lib/native';
import { DeferredImage } from './DeferredImage';

async function downscale(file: File, max = 1280, quality = 0.7): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

async function getGeo(): Promise<{ lat?: number; lng?: number }> {
  // Native: the Capacitor Geolocation plugin (true GPS + the OS permission
  // prompt). Web: the browser's geolocation. Either way we fail soft to {}.
  if (isNative) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const p = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 });
      return { lat: p.coords.latitude, lng: p.coords.longitude };
    } catch { return {}; }
  }
  return new Promise((res) => {
    if (!navigator.geolocation) return res({});
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => res({}),
      { timeout: 4000, maximumAge: 60_000 },
    );
  });
}

// Native: open the device camera via the Capacitor plugin and return a
// downscaled JPEG data URL (the plugin handles capture + resize + orientation).
async function captureNative(): Promise<string | null> {
  const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  try {
    const photo = await CapCamera.getPhoto({
      quality: 70,
      width: 1280,
      correctOrientation: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
    });
    return photo.dataUrl ?? null;
  } catch {
    return null;   // user cancelled or denied — silent, no error toast
  }
}

export function DeliveryProof({ requestId, canUpload = false }: { requestId: number; canUpload?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['proofs', requestId], queryFn: () => proofs.list(requestId) });
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  // Both paths converge on a downscaled JPEG data URL; we attach geo and upload.
  const upload = useMutation({
    mutationFn: async (image: string) => {
      const geo = await getGeo();
      return proofs.upload(requestId, { image, ...geo });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['proofs', requestId] }); toast.success('Delivery photo added'); },
    onError: (e: Error) => toast.error(e.message || 'Upload failed'),
  });

  // Web: file input -> canvas downscale. (Native uses onCapture below.)
  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await upload.mutateAsync(await downscale(file)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // Native: open the camera plugin directly.
  const onCapture = async () => {
    setBusy(true);
    try {
      const image = await captureNative();
      if (image) await upload.mutateAsync(image);
    } finally { setBusy(false); }
  };

  const onAddPhoto = () => { if (isNative) onCapture(); else fileRef.current?.click(); };

  const items = q.data ?? [];
  if (!canUpload && items.length === 0) return null;   // nothing to show viewers

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" /> Proof of delivery
        </span>
        {canUpload && (
          <>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
            <button
              onClick={onAddPhoto}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[var(--color-border)] text-[12px] font-semibold text-[var(--color-body)] hover:bg-[var(--color-panel)] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Add photo
            </button>
          </>
        )}
      </div>

      {q.isLoading ? (
        <div className="text-[12px] text-[var(--color-muted)]"><Loader2 className="inline w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-[12px] text-[var(--color-muted)]">No photo yet — capture one at hand-off so the sender can see it was received.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((p: ProofMeta) => (
            <a key={p.id} href={proofs.imageUrl(requestId, p.id)} target="_blank" rel="noreferrer" className="block group relative" title={new Date(p.created_at).toLocaleString()}>
              {/* Deferred, not hidden, in low-data mode: this photo is how a
                  sender verifies the goods arrived and is the evidence in a
                  dispute, so it stays one tap away rather than being dropped. */}
              <DeferredImage
                src={proofs.imageUrl(requestId, p.id)}
                alt="delivery proof"
                label="Delivery photo"
                className="w-24 h-24 object-cover rounded-lg border border-[var(--color-border)] group-hover:ring-2 group-hover:ring-brand-400"
              />
              {(p.lat != null && p.lng != null) && (
                <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-black/60 text-white text-[9px]"><MapPin className="w-2.5 h-2.5" /> geo</span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
